import { Loader, Modal } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Action,
    Direction,
    FirewallRule,
    NetworkInterface,
    Protocol,
    RulePosition,
} from '../../../../../@types/firewall-rule';
import { Button, InputField } from '../../../../../components/Atoms';
import { useCreateSecurityGroupRuleMutate } from '../../../../../hooks/CloudComputeHooks/useCreateSecurityGroupRuleMutate';
import { useUpdateSecurityGroupRuleMutate } from '../../../../../hooks/CloudComputeHooks/useUpdateSecurityGroupRuleMutate';

interface SecurityGroupRuleModalProps {
    opened: boolean;
    onClose: () => void;
    groupId: string;
    rule?: FirewallRule & RulePosition;
}

export default function SecurityGroupRuleModal({
    opened,
    onClose,
    groupId,
    rule,
}: SecurityGroupRuleModalProps) {
    const createMutation = useCreateSecurityGroupRuleMutate();
    const updateMutation = useUpdateSecurityGroupRuleMutate();
    const isEdit = !!rule;
    const { t } = useTranslation('cloudComputeSecurityGroup');
    const { t: tInstance } = useTranslation('cloudComputeInstance');

    const DIRECTION_OPTIONS = useMemo(
        () => [
            { value: 'in', label: tInstance('firewall.direction.inbound') },
            { value: 'out', label: tInstance('firewall.direction.outbound') },
        ],
        [tInstance]
    );

    const ACTION_OPTIONS = useMemo(
        () => [
            { value: 'accept', label: tInstance('firewall.action.accept') },
            { value: 'drop', label: tInstance('firewall.action.drop') },
            { value: 'reject', label: tInstance('firewall.action.reject') },
        ],
        [tInstance]
    );

    const PROTOCOL_OPTIONS = useMemo(
        () => [
            { value: '', label: tInstance('firewall.protocol.any') },
            { value: 'tcp', label: tInstance('firewall.protocol.tcp') },
            { value: 'udp', label: tInstance('firewall.protocol.udp') },
            { value: 'icmp', label: tInstance('firewall.protocol.icmp') },
        ],
        [tInstance]
    );

    const INTERFACE_OPTIONS = useMemo(
        () => [
            { value: 'net0', label: tInstance('firewall.interfaceOption.public') },
            { value: 'net1', label: tInstance('firewall.interfaceOption.private') },
        ],
        [tInstance]
    );

    const [direction, setDirection] = useState<Direction>('in');
    const [action, setAction] = useState<Action>('accept');
    const [protocol, setProtocol] = useState<string>('');
    const [iface, setIface] = useState<NetworkInterface>('net0');
    const [port, setPort] = useState('');
    const [source, setSource] = useState('0.0.0.0/0');
    const [destination, setDestination] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (!opened) return;
        if (rule) {
            setDirection(rule.direction);
            setAction(rule.action);
            setProtocol(rule.protocol ?? '');
            setIface(rule.iface ?? 'net0');
            setPort(rule.destination_port ?? rule.source_port ?? '');
            setSource(rule.source ?? '');
            setDestination(rule.destination ?? '');
            setDescription(rule.description ?? '');
        } else {
            setDirection('in');
            setAction('accept');
            setProtocol('');
            setIface('net0');
            setPort('');
            setSource('0.0.0.0/0');
            setDestination('');
            setDescription('');
        }
    }, [opened, rule]);

    const isSaving = createMutation.isPending || updateMutation.isPending;

    const handleSave = () => {
        const body: FirewallRule = {
            direction,
            action,
            protocol: (protocol || undefined) as Protocol | undefined,
            iface,
            source: source || undefined,
            destination: destination || undefined,
            destination_port: port || undefined,
            description: description || undefined,
        };
        if (isEdit && rule) {
            updateMutation.mutate(
                { groupId, body: { ...body, position: rule.position } },
                { onSuccess: () => onClose() }
            );
        } else {
            createMutation.mutate({ groupId, body }, { onSuccess: () => onClose() });
        }
    };

    return (
        <Modal
            size="md"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
            }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start">
                    <Modal.Title>
                        <span className="text-base font-bold">
                            {isEdit ? t('ruleModal.editTitle') : t('ruleModal.addTitle')}
                        </span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">
                        {isEdit ? t('ruleModal.editDescription') : t('ruleModal.addDescription')}
                    </p>
                </div>
                <div className="w-full flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-3">
                        <Select
                            label={t('ruleModal.directionLabel')}
                            data={DIRECTION_OPTIONS}
                            value={direction}
                            onChange={(v) => v && setDirection(v as Direction)}
                            disabled={isSaving}
                            allowDeselect={false}
                        />
                        <Select
                            label={t('ruleModal.actionLabel')}
                            data={ACTION_OPTIONS}
                            value={action}
                            onChange={(v) => v && setAction(v as Action)}
                            disabled={isSaving}
                            allowDeselect={false}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Select
                            label={t('ruleModal.protocolLabel')}
                            data={PROTOCOL_OPTIONS}
                            value={protocol}
                            onChange={(v) => setProtocol(v ?? '')}
                            disabled={isSaving}
                            allowDeselect={false}
                        />
                        <InputField
                            label={t('ruleModal.portLabel')}
                            placeholder={t('ruleModal.portPlaceholder')}
                            value={port}
                            onChange={(e) => setPort(e.target.value)}
                            disabled={isSaving}
                        />
                    </div>
                    <Select
                        label={t('ruleModal.interfaceLabel')}
                        data={INTERFACE_OPTIONS}
                        value={iface}
                        onChange={(v) => v && setIface(v as NetworkInterface)}
                        disabled={isSaving}
                        allowDeselect={false}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <InputField
                            label={t('ruleModal.sourceLabel')}
                            placeholder={t('ruleModal.cidrFieldPlaceholder')}
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            disabled={isSaving}
                        />
                        <InputField
                            label={t('ruleModal.destinationLabel')}
                            placeholder={t('ruleModal.cidrFieldPlaceholder')}
                            value={destination}
                            onChange={(e) => setDestination(e.target.value)}
                            disabled={isSaving}
                        />
                    </div>
                    <InputField
                        label={t('ruleModal.descriptionLabel')}
                        placeholder={t('ruleModal.descriptionPlaceholder')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={isSaving}
                    />
                    <div className="w-full flex flex-col justify-start items-start gap-2.5">
                        <Button width="100%" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? (
                                <div className="flex items-center gap-2">
                                    <Loader size={16} color="white" />
                                    <span>{t('ruleModal.saving')}</span>
                                </div>
                            ) : isEdit ? (
                                t('ruleModal.saveChanges')
                            ) : (
                                t('ruleModal.saveRule')
                            )}
                        </Button>
                        <Button
                            width="100%"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            {t('ruleModal.cancel')}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
